import axios from 'axios';
import { AccountInfo, PopupRequest, PublicClientApplication } from '@azure/msal-browser';
import {
  bootstrapSessionFromMicrosoftAccountTokenExchange,
  signinWithMicrosoftTokenExchange,
  TokenExchangeResponse,
} from './msalTokenExchange';

export interface BaseAuthStorage<TKey extends string> {
  getItem: (key: TKey) => string | undefined;
  setItem: (key: TKey, value: string | undefined) => void;
  removeItem: (key: TKey) => void;
}

export interface BaseAuthKeys<TKey extends string> {
  token: TKey;
  tokenExpiration: TKey;
  refreshToken: TKey;
  msalAccountHomeId: TKey;
}

export interface BaseAuthConfig<TKey extends string> {
  oauthUrl: string;
  oauthClientId: string;
  oauthScope: string;
  microsoftClientId: string;
  microsoftScopes: string[];
  missingMicrosoftClientIdMessage: string;
  missingMicrosoftScopesMessage: string;
  storage: BaseAuthStorage<TKey>;
  keys: BaseAuthKeys<TKey>;
  msalInstance: PublicClientApplication;
  msalReady: Promise<void>;
  microsoftLoginRequest: PopupRequest;
}

interface SessionResult {
  expiresIn: number;
  accessToken: string;
  refreshToken?: string;
}

export abstract class BaseAuth<TKey extends string> {
  protected readonly oauthUrl: string;
  protected readonly oauthClientId: string;
  protected readonly oauthScope: string;
  protected readonly microsoftClientId: string;
  protected readonly microsoftScopes: string[];
  protected readonly missingMicrosoftClientIdMessage: string;
  protected readonly missingMicrosoftScopesMessage: string;
  protected readonly storage: BaseAuthStorage<TKey>;
  protected readonly keys: BaseAuthKeys<TKey>;
  protected readonly msalInstance: PublicClientApplication;
  protected readonly msalReady: Promise<void>;
  protected readonly microsoftLoginRequest: PopupRequest;

  protected constructor(config: BaseAuthConfig<TKey>) {
    this.oauthUrl = config.oauthUrl;
    this.oauthClientId = config.oauthClientId;
    this.oauthScope = config.oauthScope;
    this.microsoftClientId = config.microsoftClientId;
    this.microsoftScopes = config.microsoftScopes;
    this.missingMicrosoftClientIdMessage = config.missingMicrosoftClientIdMessage;
    this.missingMicrosoftScopesMessage = config.missingMicrosoftScopesMessage;
    this.storage = config.storage;
    this.keys = config.keys;
    this.msalInstance = config.msalInstance;
    this.msalReady = config.msalReady;
    this.microsoftLoginRequest = config.microsoftLoginRequest;
  }

  protected setMsalAccountContext(account: AccountInfo): void {
    this.msalInstance.setActiveAccount(account);
    this.storage.setItem(this.keys.msalAccountHomeId, account.homeAccountId);
  }

  getBearer(): string | undefined {
    const accessToken = this.storage.getItem(this.keys.token);
    return accessToken ? `Bearer ${accessToken}` : undefined;
  }

  syncAuthenticationHeadersFromStoredToken(): void {
    const token = this.storage.getItem(this.keys.token);
    if (token) {
      this.setAuthenticationHeaders(token);
    } else {
      this.clearAuthenticationHeaders();
    }
  }

  async signinWithMicrosoft(): Promise<void> {
    if (!this.microsoftClientId) {
      throw new Error(this.missingMicrosoftClientIdMessage);
    }

    if (this.microsoftScopes.length === 0) {
      throw new Error(this.missingMicrosoftScopesMessage);
    }

    await signinWithMicrosoftTokenExchange({
      msalReady: this.msalReady,
      msalInstance: this.msalInstance,
      microsoftLoginRequest: this.microsoftLoginRequest,
      setMsalAccountContext: account => this.setMsalAccountContext(account),
      exchangeMicrosoftToken: (token, tokenType) => this.exchangeMicrosoftToken(token, tokenType),
      setSession: authResult => this.setSession(authResult),
    });
  }

  bootstrapSessionFromMicrosoftAccount(): Promise<boolean> {
    const existingToken = this.storage.getItem(this.keys.token);
    return bootstrapSessionFromMicrosoftAccountTokenExchange({
      msalReady: this.msalReady,
      msalInstance: this.msalInstance,
      microsoftLoginRequest: this.microsoftLoginRequest,
      storedHomeAccountId: this.storage.getItem(this.keys.msalAccountHomeId),
      existingToken,
      isStoredTokenValid: !!(existingToken && this.isAuthenticated()),
      onStoredTokenReuse: token => this.onStoredTokenReuse(token),
      setMsalAccountContext: account => this.setMsalAccountContext(account),
      setAuthenticationHeaders: token => this.setAuthenticationHeaders(token),
      exchangeMicrosoftToken: (token, tokenType) => this.exchangeMicrosoftToken(token, tokenType),
      setSession: authResult => this.setSession(authResult),
    });
  }

  async refreshSession(): Promise<boolean> {
    const refreshToken = this.storage.getItem(this.keys.refreshToken);
    if (!refreshToken || !this.oauthClientId) {
      return false;
    }

    const params = new URLSearchParams();
    params.append('client_id', this.oauthClientId);
    params.append('scope', this.oauthScope);
    params.append('grant_type', 'refresh_token');
    params.append('refresh_token', refreshToken);

    const response = await axios.post<TokenExchangeResponse>(
      `${this.oauthUrl}/connect/token`,
      params,
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- HTTP header name format
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      },
    );

    this.setSession({
      expiresIn: response.data.expires_in,
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token ?? refreshToken,
    });

    return true;
  }

  signOut(): void {
    const refreshToken = this.storage.getItem(this.keys.refreshToken);
    if (refreshToken) {
      void this.revokeRefreshToken(refreshToken);
    }

    this.storage.removeItem(this.keys.token);
    this.storage.removeItem(this.keys.tokenExpiration);
    this.storage.removeItem(this.keys.refreshToken);
    this.storage.removeItem(this.keys.msalAccountHomeId);

    this.onSignOut();
    this.clearAuthenticationHeaders();
  }

  isAuthenticated(): boolean {
    const expiry = this.storage.getItem(this.keys.tokenExpiration);
    if (!expiry) {
      return false;
    }

    const currentTime = new Date().getTime();
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- expiry should always be a number
    const expirationTime: number = JSON.parse(expiry) as number;
    const authenticated = currentTime < expirationTime;

    if (!authenticated) {
      this.handleExpiredToken();
    }

    return authenticated;
  }

  protected setSession(authResult: SessionResult): void {
    const expiresAt = JSON.stringify(authResult.expiresIn * 1000 + new Date().getTime());
    this.storage.setItem(this.keys.token, authResult.accessToken);
    this.storage.setItem(this.keys.tokenExpiration, expiresAt);
    this.storage.setItem(this.keys.refreshToken, authResult.refreshToken);
    this.onSessionSet(authResult.accessToken);
    this.setAuthenticationHeaders(authResult.accessToken);
  }

  protected onStoredTokenReuse(_: string): void {
    return;
  }

  protected onSessionSet(_: string): void {
    return;
  }

  protected onSignOut(): void {
    return;
  }

  protected handleExpiredToken(): void {
    return;
  }

  protected abstract setAuthenticationHeaders(token: string): void;

  protected abstract clearAuthenticationHeaders(): void;

  protected async exchangeMicrosoftToken(
    microsoftToken: string,
    subjectTokenType: string,
  ): Promise<TokenExchangeResponse> {
    if (!this.oauthClientId) {
      throw new Error(
        'Missing OAuth client id. Set VITE_REACT_APP_OAUTH_CLIENT_ID in your frontend environment.',
      );
    }

    const params = new URLSearchParams();
    params.append('client_id', this.oauthClientId);
    params.append('grant_type', 'urn:ydistri:oauth:grant-type:microsoft-token-exchange');
    params.append('subject_token', microsoftToken);
    params.append('subject_token_type', subjectTokenType);
    params.append('scope', this.oauthScope);

    const response = await axios.post<TokenExchangeResponse>(
      `${this.oauthUrl}/connect/token`,
      params,
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- HTTP header name format
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      },
    );

    return response.data;
  }

  private async revokeRefreshToken(refreshToken: string): Promise<void> {
    try {
      const params = new URLSearchParams();
      params.append('client_id', this.oauthClientId);
      params.append('token', refreshToken);
      params.append('token_type_hint', 'refresh_token');

      await axios.post(`${this.oauthUrl}/connect/revocation`, params, {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- HTTP header name format
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
    } catch {
      // Ignore revocation errors: local token/session cleanup still proceeds.
    }
  }
}
